import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Table,
  Spinner,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { Divider } from "@material-ui/core";
import { useBinnaclesStore } from "@stores/binnacles";
import { GetUser } from "@metronic/_helpers";
import { getInputClasses } from "@utils/get-input-classes";
import AlertError from "@components/shared/alert-error";

export const HospitalSupervisionBinnacleModal = ({ show, handleClose, caseId, caseData, slug }) => {
  const { user: userAuth } = GetUser();
  const { fetchAllByCase, store: storeBinnacle } = useBinnaclesStore();

  const [localBinnacles, setLocalBinnacles] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadBinnacles = async () => {
    if (!caseId || !show) return;

    setLoading(true);
    try {
      const response = await fetchAllByCase(caseId, true);
      setLocalBinnacles(response.data);
    } catch (error) {
      console.error("Error loading binnacles:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (show && caseId) {
      loadBinnacles();
    }

    return () => {
      setLocalBinnacles([]);
    };
  }, [show, caseId]);

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!show) return;

    const dataBinnacle = {
      comment: values.comment,
      event: "Supervisión Hospitalaria",
      userId: userAuth.idUser,
      caseType: caseData?.caseType || "Supervisión Hospitalaria",
      axaCategories: caseData?.axaCategories,
      movementsMedicalOpinion: caseData?.movementsMedicalOpinion,
      complexCase: caseData?.complexCase,
    };

    try {
      await storeBinnacle(dataBinnacle, caseId, slug, false);
      await loadBinnacles();
      resetForm();
    } catch (error) {
      console.error("Error saving binnacle:", error);
    }
    setSubmitting(false);
  };

  const binnaclesContent = localBinnacles?.content || localBinnacles;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bitácora de Supervisión Hospitalaria</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {show && (
          <Formik
            key={show ? "open" : "closed"}
            initialValues={{ comment: "" }}
            validationSchema={Yup.object().shape({
              comment: Yup.string()
                .required("El campo es requerido")
                .test('required', 'El campo es requerido',
                  (value) => typeof value !== 'undefined' && value.trim() !== ''
                ),
            })}
            onSubmit={onSubmit}
          >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="comment">
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comment"
                  placeholder="AGREGAR UN COMENTARIO..."
                  value={values.comment}
                  onChange={handleChange}
                  className={`${getInputClasses(
                    touched,
                    errors,
                    "comment"
                  )}`}
                  style={{ resize: 'none' }}
                />
                <ErrorMessage name="comment">
                  {(msg) => (
                    <Form.Control.Feedback type="invalid">
                      {msg}
                    </Form.Control.Feedback>
                  )}
                </ErrorMessage>
              </Form.Group>
              <Button
                variant="dark"
                type="submit"
                disabled={isSubmitting}
                className="mb-3"
              >
                {isSubmitting && (
                  <Spinner
                    as="span"
                    animation="border"
                    variant="light"
                    size="sm"
                    className="mr-2"
                  />
                )}
                AGREGAR COMENTARIO
              </Button>
            </Form>
          )}
          </Formik>
        )}

        <Divider className="mb-3" />

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {binnaclesContent?.length > 0 ? (
              binnaclesContent
                .filter(binnacle => binnacle.comment.trim() !== "")
                .map((binnacle, idx) => (
                  <div key={idx} className="d-flex mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center mr-3"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#343a40',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        flexShrink: 0
                      }}
                    >
                      {binnacle.user?.userName?.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <div className="flex-grow-1">
                      <div>
                        <strong>{binnacle.user?.userName?.toUpperCase() || 'ADMIN'}</strong>
                        <span className="text-muted ml-2" style={{ fontSize: '12px' }}>
                          {moment(binnacle.createdAt).fromNow()}
                        </span>
                      </div>
                      <div className="text-muted" style={{ fontSize: '14px' }}>
                        {binnacle.comment}
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center text-muted">
                No hay comentarios de supervisión hospitalaria
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
